import { defineStore } from 'pinia'
import { gridLayoutService } from '@/services'
import { hasCategoryOrderOverride, normalizeLayout } from '@/utils/gridLayout'
import { rewarmDrillCacheFromProducts as rewarmDrillCacheEntries } from '@/utils/drillCache'
import { useProductsStore } from '@/stores/products'
import { useCategoriesStore } from '@/stores/categories'

/**
 * POS grid layouts store.
 *
 * Key ideas:
 * - Supports multiple layouts keyed by `scope` (e.g. 'user', 'global', 'favorites').
 * - `layouts` is the cache of all known layouts; `layout` is the currently active one.
 * - `loadLayout(scope)` is cache-aware:
 *   - First call for a scope fetches from `/api/pos/grid-layout?scope=...`.
 *   - Subsequent calls with the same scope only swap `activeScope`/`layout` locally.
 * - This makes it safe to:
 *   - Preload several scopes at boot (user/global/favorites, etc.).
 *   - Switch tabs in the Sales Register UI without refetching each time.
 *
 * When adding a new grid type (e.g. 'favorites'):
 * - Ensure the backend accepts the new `scope` value.
 * - Use `gridStore.loadLayout('favorites')` from the UI/tab.
 * - Optionally preload the new scope at boot and include it in the POS snapshot.
 */

export const useGridStore = defineStore('grid', {
  state: () => ({
    activeScope: 'user',
    // Map of layouts keyed by scope (e.g. user, global, favorites)
    layouts: {},
    // Convenience reference to the currently active layout
    layout: null,
    // Drill stack: { kind: 'category'|'group', id, name }
    stack: [],
    // In-memory cache of lazily loaded drilldown tiles per category id.
    // Shape: { [categoryId]: { categories, productSlots, productTotal, total, loadedApiPages, fetchedAt } }
    // This is intentionally NOT persisted to local storage/snapshots to avoid
    // pinning stale derived data across environment/db resets.
    drillCache: {},
    pageIndex: 0,
    searchQuery: '',
    searchResults: [],
    loading: false,
    error: ''
  }),
  getters: {
    visibleCells (state) {
      const layout = state.layout
      if (!layout) return 0
      return (layout.rows || 4) * (layout.cols || 4) - 3 // minus back + two pager
    }
  },
  actions: {
    async loadLayout (scope = 'user', options = {}) {
      const { force = false } = options || {}

      // If we already have a layout for this scope, just switch to it unless forced
      if (!force && this.layouts[scope]) {
        this.activeScope = scope
        this.layout = this.layouts[scope]
        this.pageIndex = 0
        this.stack = []
        return
      }

      this.loading = true
      this.error = ''
      try {
        this.activeScope = scope
        const data = await gridLayoutService.getLayout(scope)
        const incoming = data.layout ? data.layout : data
        const layout = normalizeLayout(incoming)
        this.layouts[scope] = layout
        this.layout = layout
        this.pageIndex = 0
        this.stack = []
      } catch (e) {
        this.error = e?.response?.data?.message || 'Failed to load grid layout'
      } finally {
        this.loading = false
      }
    },
    setSearchQuery (q) {
      this.searchQuery = q
      // do not mutate results here; caller updates results after fetch
      this.pageIndex = 0
      this.stack = []
    },
    setSearchResults (items) {
      this.searchResults = items || []
      this.pageIndex = 0
    },
    async saveLayout (scope, layout) {
      this.loading = true
      this.error = ''
      try {
        const normalized = normalizeLayout(layout)
        await gridLayoutService.saveLayout(scope, normalized)
        this.layouts[scope] = normalized
        if (this.activeScope === scope) {
          this.layout = normalized
        }
        this.seedDrillCacheFromCatalog({ layout: normalized })
        const { useProductsStore } = await import('@/stores/products')
        const { usePosDataStore } = await import('@/stores/posData')
        await useProductsStore().preloadGridProducts({ layouts: this.layouts })
        await usePosDataStore().saveSnapshot()
      } catch (e) {
        this.error = e?.response?.data?.message || 'Failed to save grid layout'
        throw e
      } finally {
        this.loading = false
      }
    },
    pushDrill (context) {
      this.stack.push({
        kind: context.kind || 'category',
        id: context.id,
        name: context.name
      })
      this.pageIndex = 0
    },
    pushCategory (category) {
      this.pushDrill({ kind: 'category', id: category.id, name: category.name })
    },
    pushGroup (group) {
      this.pushDrill({ kind: 'group', id: group.refId ?? group.id, name: group.name })
    },
    popCategory () {
      this.stack.pop()
      this.pageIndex = 0
    },
    setPage (idx) {
      this.pageIndex = idx
    },
    rewarmDrillCacheFromProducts (products) {
      this.drillCache = rewarmDrillCacheEntries(this.drillCache, products)
    },
    /**
     * Build or refresh in-memory drill cache from the local catalog index.
     * Skips categories with custom categoryOrder (layout-driven drill).
     */
    seedDrillCacheFromCatalog (options = {}) {
      const productsStore = useProductsStore()
      const categoriesStore = useCategoriesStore()
      const products = options.products ?? productsStore.items

      if (!Array.isArray(products) || !products.length) return

      const flat = categoriesStore.flat || []
      const tree = categoriesStore.tree || []
      if (!flat.length || !tree.length) return

      const getCategoryById = (id) => flat.find(c => Number(c.id) === Number(id)) || null
      const layout = options.layout ?? this.layout
      const categoryIds = (options.categoryIds ?? flat.map(c => c.id))
        .filter((id) => !hasCategoryOrderOverride(layout, id))

      this.drillCache = rewarmDrillCacheEntries(this.drillCache, products, {
        getCategoryById,
        categoryTree: tree,
        categoryIds
      })
    }
  }
})
